import React, { useEffect, useMemo, useState } from 'react';

import { useFormContext } from 'react-hook-form';

import { FontAwesomeIcon } from '@appcharge/shared-ui';
import { StoreBodyBlock } from 'constants/enums';
import { motion } from 'framer-motion';
import { useTemplateStudioState } from 'state/hooks/template-studio.hook';
import { TemplateStudioBlock } from 'state/template-studio.state.service';

import { cn } from 'components/lib/utils';
import { checkNewDesignByBlock } from 'components/TemplatesStudio/utils';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger
} from 'components/ui/collapsible';
import { SidebarMenuItem } from 'components/ui/sidebar';

import { mapBlockToSecondarySidebar } from '../../../utils/blockForms.utils';

const TEMPLATE_STUDIO_STATIC_BLOCKS = {
  HEADER: 'header',
  FOOTER: 'footer'
};

export const mapBlockToTitle: Record<TemplateStudioBlock, string> = {
  [StoreBodyBlock.banners]: 'Banners',
  [StoreBodyBlock.bundles]: 'Bundles',
  [StoreBodyBlock.specialOffers]: 'Promotions',
  [StoreBodyBlock.rollingOffers]: 'Rolling Offers',
  [StoreBodyBlock.richText]: 'Rich Text',
  [StoreBodyBlock.dailyBonusBadge]: 'Daily Bonus Badge',
  [StoreBodyBlock.playerLevel]: 'Player Level',
  [StoreBodyBlock.progressBars]: 'Progress Bars',
  popup: 'Popup',
  footer: 'Footer',
  header: 'Header'
};

const mapBlockToDesignIdPath: Partial<Record<TemplateStudioBlock, string>> = {
  [StoreBodyBlock.bundles]: 'bundle.selectedDesignId',
  [StoreBodyBlock.specialOffers]: 'promotion.selectedDesignId',
  [StoreBodyBlock.rollingOffers]: 'rollingOffers.selectedDesignId'
};

const NewBadge: React.FC = () => (
  <div
    className={cn(
      'new-category-badge flex ml-2 items-center px-2 py-1 text-xs rounded-full bg-base-secondary w-[47px] h-[20px] justify-center align-middle font-semibold'
    )}
  >
    <span className={cn('text-green-500')}>{'New'}</span>
  </div>
);

export const DesignBlockMenuItem: React.FC<{
  className?: string;
  blockType: TemplateStudioBlock;
  isHidden?: boolean;
  children?: React.ReactNode;
  dragIndex?: number;
  isDragging?: boolean;
  isCurrentItemDragged?: boolean;
  dragHandleListeners?: any;
  dragHandleAttributes?: any;
}> = ({
  className,
  blockType,
  isHidden = false,
  children,
  dragIndex,
  isDragging = false,
  isCurrentItemDragged = false,
  dragHandleListeners,
  dragHandleAttributes
}) => {
  const {
    openSidebarBlock,
    setOpenSidebarBlock,
    toggleBlockVisibility,
    setScrollToSection
  } = useTemplateStudioState();

  const { setValue, watch, getValues } = useFormContext();
  const currentBodyBlocksOrder = watch('bodyBlocksOrder') || [];

  const [selectedInitialDesignId, setSelectedInitialDesignId] = useState<
    string | undefined
  >(undefined);

  useEffect(() => {
    const designIdPath = mapBlockToDesignIdPath?.[blockType] || '';
    if (designIdPath) {
      const value = getValues(designIdPath);
      setSelectedInitialDesignId(value);
    }
  }, [blockType]);

  const isOpen = useMemo(
    () => openSidebarBlock === blockType,
    [openSidebarBlock, blockType]
  );

  const hasNewDesignInTheme = useMemo(
    () =>
      selectedInitialDesignId &&
      checkNewDesignByBlock[blockType] &&
      checkNewDesignByBlock[blockType]!(selectedInitialDesignId),
    [blockType, selectedInitialDesignId]
  );

  const isDraggableBlock: boolean = useMemo(
    () => (Object.values(StoreBodyBlock) as string[]).includes(blockType),
    [blockType]
  );

  const isStoreBlock = (Object.values(StoreBodyBlock) as string[]).includes(
    blockType as string
  );
  const isBlockVisible = !isStoreBlock || !isHidden;

  const isLastVisibleBlock = useMemo(
    () =>
      isStoreBlock &&
      isBlockVisible &&
      currentBodyBlocksOrder.length === 1 &&
      currentBodyBlocksOrder[0] === blockType,
    [isStoreBlock, isBlockVisible, currentBodyBlocksOrder, blockType]
  );

  const getScrollSection = (
    shouldScroll: boolean
  ): StoreBodyBlock | string | undefined => {
    if (!shouldScroll) return undefined;
    if (isStoreBlock) return blockType;
    if (
      blockType === TEMPLATE_STUDIO_STATIC_BLOCKS.HEADER ||
      blockType === TEMPLATE_STUDIO_STATIC_BLOCKS.FOOTER
    )
      return blockType;
    return undefined;
  };

  const handleOpenChange = (isOpen: boolean) => {
    if (isStaticMenuItem) {
      if (isBlockVisible) {
        setScrollToSection(getScrollSection(true));
      }
      return setOpenSidebarBlock(undefined);
    }

    if (!isBlockVisible) {
      return setOpenSidebarBlock(undefined);
    }

    setOpenSidebarBlock(isOpen ? blockType : undefined);
    setScrollToSection(getScrollSection(isOpen));
  };

  const handleToggleVisibility = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (isStoreBlock) {
      if (isBlockVisible) {
        // hide block
        if (isOpen) {
          setOpenSidebarBlock(undefined);
        }
        const updatedOrder = currentBodyBlocksOrder.filter(
          (block: StoreBodyBlock) => block !== blockType
        );
        setValue('bodyBlocksOrder', updatedOrder, {
          shouldValidate: true,
          shouldDirty: true
        });
      } else {
        // show block
        const updatedOrder = [...currentBodyBlocksOrder, blockType];
        setValue('bodyBlocksOrder', updatedOrder, {
          shouldValidate: true,
          shouldDirty: true
        });
      }

      toggleBlockVisibility(blockType);
    }
  };

  const hasForm = React.Children.count(children) > 0;

  const hasSecondarySidebar = useMemo(
    () => mapBlockToSecondarySidebar[blockType],
    [blockType]
  );

  const isStaticMenuItem = useMemo(
    () => !hasSecondarySidebar && !hasForm,
    [hasSecondarySidebar, hasForm]
  );

  const collapseIcon = useMemo(
    () =>
      isOpen
        ? hasForm
          ? 'fa-chevron-down'
          : 'fa-chevron-right'
        : 'fa-chevron-right',
    [isOpen, hasForm]
  );

  const title = useMemo(() => mapBlockToTitle[blockType], [blockType]);

  return (
    <Collapsible
      className={cn('group/collapsible', 'my-2', className)}
      open={isOpen}
      onOpenChange={handleOpenChange}
    >
      <SidebarMenuItem className="flex flex-col">
        <CollapsibleTrigger className="flex justify-between items-center w-full text-base-mutedForeground text-xs">
          <div
            className={cn(
              'leading-5 font-sans font-medium text-base-mutedForeground flex items-center gap-1',
              isOpen && 'text-indigo-600 font-semibold'
            )}
          >
            <span
              {...(isDraggableBlock && dragHandleListeners
                ? dragHandleListeners
                : {})}
              {...(isDraggableBlock && dragHandleAttributes
                ? dragHandleAttributes
                : {})}
              className={cn(
                'inline-flex items-center',
                isDraggableBlock ? 'cursor-grab active:cursor-grabbing' : ''
              )}
            >
              <FontAwesomeIcon
                icon={`fa-solid fa-grip-dots-vertical`}
                className={cn(
                  'text-xs transition-opacity w-[12px] text-base-mutedForeground',
                  isDraggableBlock
                    ? isDragging && !isCurrentItemDragged
                      ? 'opacity-0'
                      : 'opacity-0 group-hover/collapsible:opacity-100'
                    : 'opacity-0'
                )}
              />
            </span>
            {title}
            {hasNewDesignInTheme && <NewBadge />}
          </div>
          <div className="flex items-center gap-2">
            {isStoreBlock && (
              <button
                type="button"
                onClick={
                  isLastVisibleBlock ? undefined : handleToggleVisibility
                }
                disabled={isLastVisibleBlock}
                className={cn(
                  'p-1 hover:bg-gray-100 rounded transition-colors group-hover/collapsible:opacity-100',
                  isBlockVisible ? 'opacity-0' : 'opacity-100'
                )}
                title={
                  isLastVisibleBlock
                    ? 'Store must have at least 1 block'
                    : isBlockVisible
                      ? 'Hide block'
                      : 'Show block'
                }
              >
                <FontAwesomeIcon
                  icon={`fa-regular ${isBlockVisible ? 'fa-eye' : 'fa-eye-slash'}`}
                  className={cn(
                    'text-sm text-gray-500 hover:text-gray-700',
                    isLastVisibleBlock && 'text-gray-400'
                  )}
                />
              </button>
            )}
            {!isStaticMenuItem && (
              <FontAwesomeIcon
                icon={`fa-solid ${collapseIcon}`}
                className={cn(
                  'text-xs text-base-mutedForeground',
                  isOpen && 'text-indigo-600'
                )}
              />
            )}
          </div>
        </CollapsibleTrigger>
        {hasForm && (
          <motion.div
            layout
            initial={{ height: 0 }}
            animate={{ height: isOpen ? 'auto' : 0 }}
            transition={{ duration: 0.1, ease: 'easeInOut' }}
          >
            <CollapsibleContent className="flex flex-col gap-2 my-2 mr-2 ml-4">
              {children}
            </CollapsibleContent>
          </motion.div>
        )}
      </SidebarMenuItem>
    </Collapsible>
  );
};
